import React from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { TrendingUp, AlertCircle, Users } from "lucide-react";
import { PageHeader, KpiCard, SectionCard } from "@/components/Shared";
import { profitability, costBreakdown, costDrivers, revenueTrend, clientBreakdown } from "@/data/mineData";

const tooltipStyle = {
  contentStyle: { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 },
};

export default function Financial() {
  const totalRev = clientBreakdown.reduce((s, c) => s + c.revenue, 0);
  const top3Share = clientBreakdown.slice(0, 3).reduce((s, c) => s + c.share, 0);

  return (
    <div data-testid="page-financial">
      <PageHeader eyebrow="Financial Performance" title="Profit, Cost & Customer Mix" subtitle="Revenue trajectory, cost structure, unit economics — and where revenue actually comes from." />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard testid="fin-revenue" label="Annual Revenue" value="R3.4bn" unit="2024 actuals" delta="+9.2% YoY" />
        <KpiCard testid="fin-opex" label="Operating Costs" value="R2.26bn" unit="2024 actuals" delta="+3.4% YoY" accent="amber" />
        <KpiCard testid="fin-ebitda" label="EBITDA" value="R1.15bn" unit="33.8% margin" delta="+24% YoY" accent="green" />
        <KpiCard testid="fin-cost-per-ton" label="Cost per Ton" value="R1,820" unit="ZAR / ton" delta="-2.1% YoY" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard title="Profitability Trend" description="Revenue, OpEx, EBITDA — last 5 years (ZAR millions)" className="xl:col-span-2">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={profitability} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="year" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R${v}m`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `R${v.toLocaleString()}m`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#0F172A" name="Revenue" radius={[4, 4, 0, 0]} />
                <Bar dataKey="opex" fill="#94a3b8" name="OpEx" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="ebitda" stroke="#B45309" strokeWidth={2.5} name="EBITDA" dot={{ r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Cost Breakdown" description="Share of total OpEx, 2024">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={costBreakdown} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {costBreakdown.map((c) => <Cell key={c.name} fill={c.color} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* Client Breakdown — Pie + Table */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard
          title="Revenue by Client"
          description={`Top 3 customers = ${top3Share}% of revenue`}
          action={<Users size={16} className="text-amber-700" />}
        >
          <div className="h-80" data-testid="client-pie-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={clientBreakdown} dataKey="share" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {clientBreakdown.map((c) => <Cell key={c.name} fill={c.color} />)}
                </Pie>
                <Tooltip
                  {...tooltipStyle}
                  formatter={(v, _n, p) => [`${v}% · R${p.payload.revenue.toLocaleString()}m`, p.payload.name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Customer Concentration" description="Detailed offtake, contract terms and commodity" className="xl:col-span-2">
          <div className="overflow-x-auto" data-testid="client-breakdown-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-4 font-semibold">Client</th>
                  <th className="py-3 pr-4 font-semibold">Commodity</th>
                  <th className="py-3 pr-4 font-semibold">Contract</th>
                  <th className="py-3 pr-4 font-semibold text-right">Revenue</th>
                  <th className="py-3 pr-4 font-semibold text-right w-32">Share</th>
                </tr>
              </thead>
              <tbody>
                {clientBreakdown.map((c) => (
                  <tr key={c.name} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`client-row-${c.name.split(" ")[0].toLowerCase()}`}>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: c.color }} />
                        <span className="font-medium text-slate-900">{c.name}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-700 text-xs">{c.commodity}</td>
                    <td className="py-3 pr-4 text-slate-700 text-xs">{c.contract}</td>
                    <td className="py-3 pr-4 text-right font-display font-semibold text-slate-900">
                      R{c.revenue.toLocaleString()}m
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full" style={{ width: `${c.share}%`, background: c.color }} />
                        </div>
                        <span className="text-xs text-slate-700 font-semibold w-10 text-right">{c.share}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50">
                  <td className="py-3 pr-4 font-semibold text-slate-900">Total</td>
                  <td className="py-3 pr-4 text-slate-500 text-xs">—</td>
                  <td className="py-3 pr-4 text-slate-500 text-xs">6 customers</td>
                  <td className="py-3 pr-4 text-right font-display font-bold text-slate-900">
                    R{totalRev.toLocaleString()}m
                  </td>
                  <td className="py-3 pr-4 text-right font-semibold text-slate-900">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard title="Revenue Trend" description="Quarterly performance" className="xl:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={revenueTrend} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="quarter" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `R${v}m`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `R${v.toLocaleString()}m`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#0F172A" name="Revenue" radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="ebitda" stroke="#B45309" strokeWidth={2.5} name="EBITDA" dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Key Cost Drivers" description="Sensitivity factors" action={<AlertCircle size={16} className="text-amber-600" />}>
          <ul className="space-y-3">
            {costDrivers.map((d, i) => (
              <li key={i} className="border-l-2 border-amber-600 pl-3 py-1" data-testid={`cost-driver-${i}`}>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{d.label}</div>
                <div className="text-sm text-slate-800 mt-0.5">{d.value}</div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      <div className="mt-6 bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-6 text-white relative overflow-hidden">
        <div className="absolute right-6 top-6 opacity-30">
          <TrendingUp size={64} className="text-amber-400" />
        </div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-400 mb-2">
          Financial summary
        </div>
        <h3 className="font-display text-xl font-semibold tracking-tight max-w-2xl">
          Tzaneen has delivered consistent EBITDA expansion, with margins improving from 22.6% (2020) to 33.8% (2024).
        </h3>
        <p className="text-sm text-slate-300 mt-3 max-w-2xl leading-relaxed">
          Cost structure is balanced across labour, energy and maintenance — providing multiple optimisation levers for the next operator.
        </p>
      </div>
    </div>
  );
}
